import * as dat from 'dat.gui';
import { ShortcutTriggers } from './ShortcutTriggers';

const TRIGGER_TOPLEFT = '↑ ↑ ↓ ↓ ← →';
const TRIGGER_BOTTOMLEFT = '↓ ↓ ↑ ↑ ← →';
/**
 * @alias components.core.ShortcutsPlugin
 * @classdesc Uses Dat.gui to provide a shortcuts menu used to assist with debugging and testing
 * Once installed the menu is opened via arrow key or swipe => up,up,down,down,left,right
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.shortcuts
 * </pre>
 *
 * containerId
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', ShortcutsPlugin, true, 'scenekey' || null );
 * </pre>
 *
 */
class ShortcutsPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);

    this._containerID = false;
    this.model = {};
    this.groups = {};
    this.defaultGroup = 'game';
    this.pos = 'topLeft';
    this.controls = [];
    this.isVisible = __WATCH__;
    this.boundToggleTopLeft = this.toggleDisplayTopLeft.bind(this);
    this.boundToggleBottomLeft = this.toggleDisplayBottomLeft.bind(this);
    this.triggerTopLeft = new ShortcutTriggers({
      sequence: TRIGGER_TOPLEFT,
      onComplete: this.boundToggleTopLeft,
      onFail: (val) => {
        // console.log('FAIL', val);
      },
    });
    this.triggerBottomLeft = new ShortcutTriggers({
      sequence: TRIGGER_BOTTOMLEFT,
      onComplete: this.boundToggleBottomLeft,
      onFail: (val) => {
        // console.log('FAIL', val);
      },
    });
  }

  init(config) {
    if (config) {
      this._containerID = config.containerId || false;
      if (config.enabled) this.enable();
    }
  }

  /** enable shortcuts, creating the menu */
  enable() {
    if (!this.gui) this._create(this._containerID);
  }

  /** show/hide the shortcuts menu */
  toggleDisplayTopLeft() {
    if (this.pos == 'topLeft' || !this.isVisible) {
      this.isVisible ? this.hide() : this.show();
    }
    this.gui.domElement.style.top = 0;
    this.gui.domElement.style.bottom = '';
    this.pos = 'topLeft';
  }

  /** show/hide the shortcuts menu */
  toggleDisplayBottomLeft() {
    if (this.pos == 'bottomLeft' || !this.isVisible) {
      this.isVisible ? this.hide() : this.show();
    }
    this.gui.domElement.style.top = '';
    this.gui.domElement.style.bottom = '20px';
    this.pos = 'bottomLeft';
  }

  /** open the shortcuts menu */
  open() {
    if (this.gui) this.gui.open();
  }

  /** close ethe shortcuts menu */
  close() {
    if (this.gui) this.gui.close();
  }

  /** show the shortcuts menu */
  show() {
    this.isVisible = true;
    if (this.gui) this.gui.show();
  }

  /** hide the shortcuts menu */
  hide() {
    this.isVisible = false;
    if (this.gui) this.gui.hide();
  }

  /** show / hide the shortcuts menu */
  setVisible() {
    this.isVisible ? this.show() : this.hide();
  }

  /** @private */
  _create(containerId) {
    dat.GUI.TEXT_CLOSED = 'Close Shortcuts';
    dat.GUI.TEXT_OPEN = 'Open Shortcuts';
    this.gui = new dat.GUI({ name: 'Shortcuts', closed: false, autoPlace: false });
    this.gui.domElement.id = 'gui';
    this.gui.domElement.style.position = 'absolute';
    this.gui.domElement.style.top = 0;
    this.gui.domElement.setAttribute('aria-hidden', true);
    if (containerId) {
      document.getElementById(containerId).appendChild(this.gui.domElement);
    } else {
      this.game.scale.parent.appendChild(this.gui.domElement);
    }
    this.addShortcutGroup({ title: 'Game', open: true });
    this.close();
    this.setVisible();
  }

  /**
   * Add a new shortcut group, grouped shortcuts will be nested in their own collapsable menus
   * @param {Object} options
   * @param {string} options.title The title of the group to add
   * @param {string} [options.id='game'] The id of the group to add
   * @param {bool} [options.open='false'] If the added group should be expanded or collapsed
   * @returns created shortcut group
   */
  addShortcutGroup({ id = this.defaultGroup, title, open = false }) {
    if (!this.gui) return;
    const group = this.gui.addFolder(title);
    this.groups[id] = group;
    open && group.open();
    return group;
  }

  addDisplayField(opts) {
    if (!this.gui) return;
    const control = this.addShortcut(opts);
    const el = control.domElement.getElementsByTagName('input')[0]; // make input read only
    el && (el.readOnly = true);
    return control;
  }

  /**
   * Add a basic shortcut
   * @param {Object} options
   * @param {string} options.field The id of the shortcut
   * @param {string} options.title The title of the shortcut
   * @param {function} options.value The shortcut callback
   * @param {string} [options.group='game'] The id of the group to add shortcut to.
   * @returns created shortcut control
   */
  addShortcut({ group = this.defaultGroup, field, title, value = null }) {
    if (!this.gui) return;
    this.model[field] = value;
    const control = this.groups[group].add(this.model, field).name(title);
    this.controls.push(control);
    return control;
  }

  /**
   * Add a slider shortcut
   * @param {Object} options
   * @param {string} options.field The id of the shortcut
   * @param {string} options.title The title of the shortcut
   * @param {number} options.value The shortcut value
   * @param {number} options.min The minimum value of the slider
   * @param {number} options.max The maximum value of the slider
   * @param {function} options.onChange The shortcut callback
   * @param {number} [options.step=0.1] The steps of the slider
   * @param {string} [options.group='game'] The id of the group to add shortcut to.
   * @returns created shortcut control
   */
  addSlider({
    group = this.defaultGroup,
    field,
    title,
    value = false,
    min,
    max,
    step = 0.1,
    onChange = null,
  }) {
    if (!this.gui) return;
    this.model[field] = value;
    const control = this.groups[group].add(this.model, field, min, max, step).name(title);
    onChange && control.onChange(onChange);
    this.controls.push(control);
    return control;
  }

  /**
   * Add a toggle shortcut
   * @param {Object} options
   * @param {string} options.field The id of the shortcut
   * @param {string} options.title The title of the shortcut
   * @param {bool} options.value The shortcut value
   * @param {function} options.onChange The shortcut callback
   * @param {string} [options.group='game'] The id of the group to add shortcut to.
   * @returns created shortcut control
   */
  addToggle({ group = this.defaultGroup, field, title, value = false, onChange = null }) {
    if (!this.gui) return;
    this.model[field] = value;
    const control = this.groups[group].add(this.model, field).name(title);
    onChange && control.onChange(onChange);
    this.controls.push(control);
    return control;
  }

  /**
   * Add a drodown shortcut
   * @param {Object} options
   * @param {string} options.field The id of the shortcut
   * @param {string} options.title The title of the shortcut
   * @param {string[]} options.value An array of options to choose from
   * @param {function} options.onChange The shortcut callback
   * @param {string} [options.group='game'] The id of the group to add shortcut to.
   * @returns created shortcut control
   */
  addDropDown({ group = this.defaultGroup, field, title, value = [], onChange = null }) {
    if (!this.gui) return;
    this.model[field] = value;
    const control = this.groups[group].add(this.model, field, value).name(title);
    onChange && control.onChange(onChange);
    this.controls.push(control);
    return control;
  }

  /**
   * Add a input shortcut
   * @param {Object} options
   * @param {string} options.field The id of the shortcut
   * @param {string} options.title The title of the shortcut
   * @param {string} [options.value=''] Default value
   * @param {function} options.onChange The shortcut callback
   * @param {string} [options.group='game'] The id of the group to add shortcut to.
   * @returns created shortcut control
   */
  addInput({ group = this.defaultGroup, field, title, value = '', onChange }) {
    this.model[field] = value;
    const control = this.groups[group].add(this.model, field).name(title);
    onChange && control.onFinishChange(onChange);
    this.controls.push(control);
    return control;
  }

  /**
   * Update shortcut components
   * Call then when an external setting changes you want synced
   */
  updateDisplay() {
    this.controls.forEach((item) => {
      item.updateDisplay();
    });
  }

  /**
   * Reset shortcuts - wipes everything for a clean slate
   */
  reset() {
    this.controls.forEach((item) => {
      item.remove();
    });
    this.controls = [];
    Object.keys(this.groups).forEach((id) => {
      if (this.gui) this.gui.removeFolder(this.groups[id]);
    });
    this.groups = {};
  }

  /**
   * Detroy shortcuts - including menu
   */
  destroy() {
    this.reset();

    if (this.gui) {
      this.gui.destroy();
      this.gui = null;
    }

    if (this.triggerTopLeft) {
      this.triggerTopLeft.destroy();
      this.triggerTopLeft = null;
    }

    if (this.triggerBottomLeft) {
      this.triggerBottomLeft.destroy();
      this.triggerBottomLeft = null;
    }
  }
}

export { ShortcutsPlugin };
